/**
 * Iris's Reduce PPC card: to slow sales while stock is low she pauses the
 * Sponsored Products ads with the highest ACoS instead of raising the price.
 * Rendered for the "awaiting" (needs the seller's OK) and "manual" (Amazon
 * Ads left some ads enabled) states of getIrisPpcState; the "applied" and
 * "reverted" ones are rows in the Done for you section, see
 * getIrisPpcDoneLines.
 */
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useCanRespondToDecisions from "../../../../../hooks/useCanRespondToDecisions";
import {
  clearRuleEngineResponse,
  respondToRuleEngineDecision,
  storeAnalyticsSelector,
  upsertProductComment,
} from "../../../../../redux/slices/store_analytics";
import { trackEvent } from "../../../../../utils/analytics";
import { AGENT_INFO, getIrisPpcState } from "../../constants";

import IrisDecisionTreeModal from "./IrisDecisionTreeModal/IrisDecisionTreeModal";

const CAMPAIGN_MANAGER_URL = "https://advertising.amazon.com/campaign-manager";

// A failed card only reaches the seller after Iris's automation gave up,
// which is always after 3 attempts
const MANUAL_ACTION_ATTEMPTS = 3;

const REJECT_REASONS = [
  "Ranking campaign",
  "Promotion running",
  "Restock arriving",
  "Other",
];

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const percent = (part, whole) =>
  whole ? Math.round((part / whole) * 100) : null;

const formatMoney = (value, currency) => {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `$${value}`;
  }
};

// ACoS comes as a ratio; null when the ad spent without selling
const formatAcos = (acos) =>
  acos == null ? "No sales" : `${Math.round(acos * 100)}%`;

// The backend sends ids only for now; names are used once it sends them
const getAdLabel = (ad) => {
  const names = [ad.campaign_name, ad.ad_group_name].filter(Boolean);
  if (names.length > 0) return names.join(" · ");
  if (ad.sku) return `SKU ${ad.sku}`;
  return `Campaign ${ad.campaign_id}`;
};

const isPaused = (ad) => ad.state_after === "PAUSED";

// The numbers of the plan, from the card when the backend put them there
// (awaiting approval) and from Iris's plan otherwise
const getPlanNumbers = (decision) => {
  const card = decision?.ppc_card || {};
  const plan = decision?.decision?.ppc_pause_plan || {};
  const unitsCovered = card.units_covered ?? plan.units_covered;
  const sponsoredUnits = card.sponsored_units ?? plan.sponsored_units;
  return {
    unitsCovered,
    sponsoredUnits,
    cutPct: percent(unitsCovered, sponsoredUnits),
    windowDays: card.window_days ?? plan.window_days,
    enabledAds: plan.enabled_ads,
  };
};

// { message, note } for a Done for you row, or null when it's no PPC card
export const getIrisPpcDoneLines = (decision) => {
  const state = getIrisPpcState(decision);
  const ads = decision?.ppc_card?.ads || [];
  if (state === "reverted") {
    return {
      message: `Iris paused ${plural(
        ads.length,
        "Sponsored Products ad"
      )} while stock was low`,
      note: "Stock recovered, so Iris turned them back on.",
    };
  }
  if (state !== "applied") return null;
  const paused = ads.filter(isPaused);
  const pausedAds = paused.length > 0 ? paused : ads;
  const named = pausedAds.filter((ad) => ad.campaign_name);
  const cutShare = decision?.decision?.ppc_pause_plan?.cut_share;
  return {
    message: `Iris paused ${plural(
      pausedAds.length,
      "Sponsored Products ad"
    )} to slow sales${
      named.length === pausedAds.length && named.length > 0
        ? `: ${named.map((ad) => ad.campaign_name).join(", ")}`
        : ""
    }`,
    note: `${
      cutShare != null
        ? `Sponsored units should drop about ${Math.round(cutShare * 100)}%. `
        : ""
    }Iris turns them back on when stock recovers.`,
  };
};

const buttonSx = { textTransform: "none", fontWeight: 500, borderRadius: 3 };

const cellSx = {
  py: 1,
  px: 1,
  borderBottom: "1px solid #f0f0f0",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const headCellSx = {
  ...cellSx,
  py: 0.75,
  fontWeight: 600,
  color: "#666",
  borderBottom: "1px solid #e0e0e0",
};

const Stat = ({ label, value }) => (
  <Typography variant="body2" sx={{ color: "#666", fontStyle: "italic" }}>
    {label}:{" "}
    <strong
      style={{
        fontStyle: "normal",
        fontWeight: 700,
        color: "#000",
        marginLeft: 4,
      }}
    >
      {value}
    </strong>
  </Typography>
);

const AdsTable = ({ ads, manual, currency, windowDays }) => (
  <Box
    component="table"
    sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}
  >
    <thead>
      <tr>
        <Box component="th" sx={{ ...headCellSx, pl: 0, textAlign: "left" }}>
          Campaign · ad group
        </Box>
        {!manual && (
          <>
            <Box component="th" sx={headCellSx}>
              Spend{windowDays ? ` ${windowDays}d` : ""}
            </Box>
            <Box component="th" sx={headCellSx}>
              Units
            </Box>
          </>
        )}
        <Box component="th" sx={{ ...headCellSx, pr: manual ? 1 : 0 }}>
          ACoS
        </Box>
        {manual && (
          <Box component="th" sx={{ ...headCellSx, pr: 0 }}>
            Status
          </Box>
        )}
      </tr>
    </thead>
    <tbody>
      {ads.map((ad) => (
        <tr key={ad.ad_id}>
          <Box
            component="td"
            sx={{
              ...cellSx,
              pl: 0,
              textAlign: "left",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
          >
            {getAdLabel(ad)}
          </Box>
          {!manual && (
            <>
              <Box component="td" sx={cellSx}>
                {formatMoney(ad.spend, currency)}
              </Box>
              <Box component="td" sx={cellSx}>
                {ad.units ?? "—"}
              </Box>
            </>
          )}
          <Box component="td" sx={{ ...cellSx, pr: manual ? 1 : 0 }}>
            {formatAcos(ad.acos)}
          </Box>
          {manual && (
            <Box
              component="td"
              sx={{
                ...cellSx,
                pr: 0,
                color: isPaused(ad) ? "#2e7d32" : "#c62828",
              }}
            >
              {isPaused(ad)
                ? "Paused"
                : `Still enabled · ${MANUAL_ACTION_ATTEMPTS} attempts failed`}
            </Box>
          )}
        </tr>
      ))}
    </tbody>
  </Box>
);

const IrisPpcCard = ({
  decision,
  asin,
  sku,
  store,
  marketplace,
  onRemove,
  // The Store Analysis popover: the suggestion headline and a short ad list
  // instead of the explanation and the ads table
  compact = false,
}) => {
  const dispatch = useDispatch();
  const { saveRuleEngineResponseLoading, saveRuleEngineResponseStatus } =
    useSelector(storeAnalyticsSelector);
  const userAllowedToRespond = useCanRespondToDecisions(store);
  const inputRef = useRef(null);
  // null, "confirm" (accept) or "reject"
  const [step, setStep] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  // What was answered, for the note under the saved response
  const [answered, setAnswered] = useState(null);
  const [showDecisionTree, setShowDecisionTree] = useState(false);

  const executionId = decision.execution_id;
  const statusKey = `${executionId}_${asin}_iris`;
  const loading = !!saveRuleEngineResponseLoading[statusKey];
  const response = saveRuleEngineResponseStatus[statusKey];

  const card = decision.ppc_card || {};
  const manual = getIrisPpcState(decision) === "manual";
  const ads = card.ads || [];
  // Retrying only touches the ads Amazon left enabled
  const adsToPause = manual ? ads.filter((ad) => !isPaused(ad)) : ads;
  const { unitsCovered, sponsoredUnits, cutPct, windowDays, enabledAds } =
    getPlanNumbers(decision);

  const action = decision.decision?.action || "";
  const warningText = action.match(/^(.+?WARNING):/i)?.[1] || null;
  const isVeryLow = /very low inventory/i.test(action);
  const daysOfSupply = decision.features?.DaysOfSupply;

  useEffect(() => {
    if (step === "reject") setTimeout(() => inputRef.current?.focus(), 0);
  }, [step]);

  const adsCount = plural(adsToPause.length, "ad");
  const adsToPauseValue = enabledAds
    ? `${adsToPause.length} of ${enabledAds}`
    : `${adsToPause.length}`;
  const unitsCut =
    unitsCovered != null && sponsoredUnits
      ? compact
        ? `${cutPct}% (${unitsCovered} of ${sponsoredUnits}${
            windowDays ? `, ${windowDays} days` : ""
          })`
        : `${unitsCovered} of ${sponsoredUnits} (${cutPct}%)${
            windowDays ? ` over ${windowDays} days` : ""
          }`
      : null;

  // Documents the answer in the product's comments, like the price cards do
  const addComment = (text) => {
    const today = new Date();
    const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    dispatch(
      upsertProductComment(
        {
          asin,
          store,
          cell: today.toISOString().slice(5, 10),
          text,
          user: "Agent iris ",
        },
        store,
        startDate,
        today.toISOString().split("T")[0],
        marketplace
      )
    );
  };

  const respond = async (userResponse) => {
    const accepted = userResponse === "Accept";
    trackEvent(
      accepted ? "recommendation_accepted" : "recommendation_rejected",
      {
        asin,
        store,
        decision_type: decision.decision_type,
        execution_id: executionId,
      }
    );
    const result = await dispatch(
      respondToRuleEngineDecision({
        asin,
        sku,
        store,
        marketplace,
        execution_id: executionId,
        status_key: statusKey,
        user_response: userResponse,
        decision_type: decision.decision_type,
        reject_reason: accepted ? null : rejectReason,
      })
    );
    if (!result.success) return;
    setAnswered(userResponse);
    setStep(null);
    const what = `pause ${adsCount}${
      unitsCovered != null ? ` (${unitsCovered} sponsored units)` : ""
    }`;
    addComment(
      accepted
        ? `Accepted ppc recommendation: ${what}`
        : `Rejected ppc recommendation: ${what} because '${rejectReason}'`
    );
    setRejectReason("");
  };

  if (response?.message) {
    return (
      <Box sx={{ py: 1, px: compact ? 0 : 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: response.success ? "#4caf50" : "#f44336",
            fontWeight: 500,
            py: 1,
          }}
        >
          {response.message}
        </Typography>
        {response.success && answered && (
          <Typography variant="caption" sx={{ color: "#757575" }} component="p">
            {answered === "Accept"
              ? `Iris pauses the ${adsCount} on its next run.`
              : "No ads were changed."}
          </Typography>
        )}
        {onRemove && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              trackEvent("recommendation_dismissed", {
                asin,
                store,
                execution_id: executionId,
              });
              dispatch(
                clearRuleEngineResponse({
                  execution_id: executionId,
                  status_key: statusKey,
                })
              );
              onRemove();
            }}
            sx={{ ...buttonSx, mt: 1, color: "#666", borderColor: "#e0e0e0" }}
          >
            Close
          </Button>
        )}
      </Box>
    );
  }

  const busy = loading || step !== null;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        py: compact ? 0 : 1,
        // Same inset as the Iris price cards next to it in the accordion
        pl: compact ? 0 : 2,
        pr: compact ? 6 : 2,
      }}
    >
      {compact && (
        <img
          src={AGENT_INFO.iris.avatar}
          alt=""
          style={{
            position: "absolute",
            right: -31,
            bottom: -20,
            width: 100,
            height: 100,
            opacity: 0.7,
          }}
        />
      )}

      {compact && (
        <Box sx={{ position: "relative" }}>
          {warningText && (
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#d32f2f", fontSize: "0.8rem" }}
            >
              {warningText}
            </Typography>
          )}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 400, color: "#666", fontStyle: "italic" }}
          >
            I Suggest to Reduce Velocity
            <span
              style={{
                fontSize: "0.9125rem",
                fontWeight: "bold",
                fontStyle: "normal",
                color: "black",
                marginLeft: "0.25rem",
              }}
            >
              Reduce PPC:
            </span>
          </Typography>
        </Box>
      )}

      {manual ? (
        <Box
          sx={{
            p: 1.5,
            backgroundColor: "#ffebee",
            border: "1px solid #ef9a9a",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#c62828", fontWeight: 500 }}
          >
            We tried to pause {adsCount} automatically, but Amazon Ads didn't
            accept the change. Accept to try again, or pause{" "}
            {adsToPause.length === 1 ? "it" : "them"} yourself in{" "}
            <Link
              href={CAMPAIGN_MANAGER_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: "inherit" }}
            >
              Campaign Manager
            </Link>
            .
          </Typography>
        </Box>
      ) : (
        !compact && (
          <Typography variant="body2" sx={{ color: "#333", lineHeight: 1.5 }}>
            {`Stock is ${isVeryLow ? "very low" : "low"}${
              daysOfSupply != null
                ? `: ${plural(Math.round(daysOfSupply), "day")} of supply`
                : ""
            }.`}{" "}
            Pausing the{" "}
            {adsToPause.length === 1 ? "ad" : `${adsToPause.length} ads`} with
            the highest ACoS cuts sponsored units
            {cutPct != null ? ` by about ${cutPct}%` : ""}. The price stays the
            same.
          </Typography>
        )
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Stat label="Ads to pause" value={adsToPauseValue} />
        {unitsCut && <Stat label="Sponsored units cut" value={unitsCut} />}
      </Box>

      {compact ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {adsToPause.map((ad) => (
            <Typography
              key={ad.ad_id}
              variant="body2"
              sx={{ fontSize: "0.78rem" }}
            >
              {getAdLabel(ad)}{" "}
              <span style={{ color: "#757575" }}>
                · ACoS {formatAcos(ad.acos)}
              </span>
            </Typography>
          ))}
        </Box>
      ) : (
        <AdsTable
          ads={ads}
          manual={manual}
          currency={card.currency}
          windowDays={windowDays}
        />
      )}

      {manual && (
        <Typography
          variant="caption"
          sx={{ color: "#757575", fontStyle: "italic" }}
        >
          Iris tried {MANUAL_ACTION_ATTEMPTS} times before asking you.
        </Typography>
      )}

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          size="small"
          disabled={!userAllowedToRespond || busy || adsToPause.length === 0}
          onClick={() => {
            trackEvent("recommendation_accept_clicked", {
              asin,
              store,
              decision_type: decision.decision_type,
            });
            setStep("confirm");
          }}
          sx={{
            ...buttonSx,
            backgroundColor: "#4caf50",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#45a049", boxShadow: "none" },
          }}
        >
          Accept
        </Button>
        <Button
          variant="outlined"
          size="small"
          disabled={!userAllowedToRespond || busy}
          onClick={() => setStep("reject")}
          sx={{
            ...buttonSx,
            color: "#f44336",
            borderColor: "#f44336",
            "&:hover": {
              borderColor: "#d32f2f",
              backgroundColor: "transparent",
            },
          }}
        >
          Reject
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            trackEvent("recommendation_reasoning_toggle", {
              asin,
              store,
              show: true,
            });
            setShowDecisionTree(true);
          }}
          sx={{
            ...buttonSx,
            borderRadius: 20,
            color: "#64b5f6",
            borderColor: "#64b5f6",
            "&:hover": {
              borderColor: "#42a5f5",
              backgroundColor: "transparent",
            },
          }}
        >
          Why?
        </Button>
      </Box>

      {!userAllowedToRespond && (
        <Typography
          variant="body2"
          sx={{ color: "red", fontStyle: "italic", textAlign: "center" }}
        >
          Only the store owner can accept or reject recommendations.
        </Typography>
      )}

      {step === "confirm" && (
        <Box sx={{ position: "relative" }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            {manual
              ? `Try pausing the ${adsCount} again?`
              : `Confirm pausing ${adsCount}?`}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={loading}
              onClick={() => respond("Accept")}
              sx={{
                ...buttonSx,
                backgroundColor: "#4caf50",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#45a049", boxShadow: "none" },
              }}
            >
              Yes
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={loading}
              onClick={() => setStep(null)}
              sx={{ ...buttonSx, color: "#666" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {step === "reject" && (
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Divider sx={{ mb: 0.75 }} />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {REJECT_REASONS.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                variant="outlined"
                disabled={loading}
                onClick={() => {
                  setRejectReason(label === "Other" ? "" : `${label}: `);
                  inputRef.current?.focus();
                }}
                sx={{ fontSize: "0.75rem" }}
              />
            ))}
          </Box>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            value={rejectReason}
            disabled={loading}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reject reason"
            inputProps={{ "aria-label": "Reject reason" }}
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fff" } }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              disabled={loading || !rejectReason.trim()}
              onClick={() => respond("Reject")}
              sx={{
                ...buttonSx,
                backgroundColor: "#f44336",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#d32f2f", boxShadow: "none" },
              }}
            >
              Submit
            </Button>
            <Button
              variant="text"
              size="small"
              disabled={loading}
              onClick={() => {
                setStep(null);
                setRejectReason("");
              }}
              sx={{ ...buttonSx, color: "#666" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      <IrisDecisionTreeModal
        open={showDecisionTree}
        onClose={() => setShowDecisionTree(false)}
        asin={asin}
        executionId={executionId}
        store={store}
        marketplace={marketplace}
      />
    </Box>
  );
};

export default IrisPpcCard;
